// Controlled Documents — lean register for the M&P + controlled records. All authenticated users (incl.
// Auditor) can view/download; Admin/QM manage documents and revisions. Files private (signed URLs).
// Only the revision marked "Current" is the controlled copy. Approval of a revision is a recorded
// action: AuditPM stamps the approver from their own login (approve_controlled_revision), the approver
// cannot be the uploader, and the approval fields are locked at the database against direct edits.
import React, { useEffect, useState } from 'react'
import { supabase } from '../lib/supabase'
import { useAuth } from '../lib/AuthContext'
import { format, parseISO } from 'date-fns'
import { toast, failed } from '../lib/toast'

const DOC_TYPES = ['M&P Manual', 'Policy', 'Procedure', 'Form', 'Template', 'Register', 'External Reference', 'Other']
const DOC_STATUS = ['Active', 'Superseded', 'Withdrawn', 'Draft']
// Roles allowed to approve a controlled-document revision. Mirrors approve_controlled_revision() in SQL.
const APPROVER_ROLES = ['Top Management', 'Managing Director', 'Quality Manager (Primary)']

function fmt(d) { return d ? format(parseISO(d), 'd MMM yyyy') : '—' }

async function openFile(rev, download) {
  if (!rev?.file_path) { toast('No file attached', 'info', { detail: 'This revision has no file' }); return }
  const opts = download ? { download: rev.file_name || true } : undefined
  const { data, error } = await supabase.storage.from('controlled-documents').createSignedUrl(rev.file_path, 600, opts)
  if (error || !data?.signedUrl) { failed("Couldn't open the file", error?.message); return }
  window.open(data.signedUrl, '_blank', 'noopener')
}

export default function ControlledDocuments() {
  const { canApprove } = useAuth()
  const [docs, setDocs] = useState([])
  const [currentRevs, setCurrentRevs] = useState({})   // document_id → current revision
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState({ type: '', status: '' })
  const [open, setOpen] = useState(null)       // open document
  const [creating, setCreating] = useState(false)

  useEffect(() => { load() }, [])
  async function load() {
    setLoading(true)
    const [{ data: d, error }, { data: rv }] = await Promise.all([
      supabase.from('controlled_documents').select('*').order('document_code', { nullsFirst: false }),
      supabase.from('controlled_document_revisions').select('*').eq('status', 'Current'),
    ])
    if (error) console.warn('controlled_documents load error (run CONTROLLED_DOCUMENTS.sql):', error.message)
    const map = {}; (rv || []).forEach(r => { map[r.document_id] = r })
    setDocs(d || []); setCurrentRevs(map); setLoading(false)
  }

  const shown = docs.filter(x => (!filter.type || x.document_type === filter.type) && (!filter.status || x.status === filter.status))

  return (
    <div>
      <div className="page-header">
        <h1>Controlled Documents</h1>
        {canApprove && <button className="primary" onClick={() => setCreating(true)}>+ New document</button>}
      </div>

      <div className="notice notice-info" style={{ fontSize: 13, marginBottom: 14 }}>
        Only the revision marked <strong>Current</strong> in AuditPM is treated as the controlled copy. Printed or downloaded copies are uncontrolled unless verified against AuditPM.
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
        <select value={filter.type} onChange={e => setFilter({ ...filter, type: e.target.value })} style={{ padding: '5px 10px', fontSize: 13 }}>
          <option value="">All types</option>{DOC_TYPES.map(x => <option key={x}>{x}</option>)}
        </select>
        <select value={filter.status} onChange={e => setFilter({ ...filter, status: e.target.value })} style={{ padding: '5px 10px', fontSize: 13 }}>
          <option value="">All statuses</option>{DOC_STATUS.map(x => <option key={x}>{x}</option>)}
        </select>
      </div>

      <div className="card" style={{ padding: 0 }}>
        <div className="table-wrap">
          <table>
            <thead><tr><th>Code</th><th>Title</th><th>Type</th><th>Current rev</th><th>Effective</th><th>Status</th><th></th></tr></thead>
            <tbody>
              {loading && <tr><td colSpan={7} style={{ color: 'var(--text3)', textAlign: 'center', padding: 24 }}>Loading…</td></tr>}
              {!loading && shown.length === 0 && <tr><td colSpan={7} style={{ color: 'var(--text3)', textAlign: 'center', padding: 24 }}>No controlled documents.</td></tr>}
              {shown.map(d => {
                const cur = currentRevs[d.id]
                return (
                  <tr key={d.id} style={{ cursor: 'pointer' }} onClick={() => setOpen(d)}>
                    <td style={{ fontWeight: 500 }}>{d.document_code || '—'}</td>
                    <td style={{ fontSize: 13 }}>{d.title || '—'}</td>
                    <td style={{ fontSize: 13, color: 'var(--text2)' }}>{d.document_type || '—'}</td>
                    <td style={{ fontSize: 13 }}>{cur ? `Rev ${cur.revision_no || '—'}` : '—'}</td>
                    <td style={{ fontSize: 13, color: 'var(--text2)' }}>{fmt(cur?.effective_date)}</td>
                    <td><span className={`badge ${d.status === 'Active' ? 'badge-ok' : d.status === 'Withdrawn' ? 'badge-warn' : 'badge'}`}>{d.status}</span>
                      {cur && cur.approval_status !== 'Approved' && cur.approval_status !== 'Approved (historical record)' && <span className="badge badge-warn" style={{ marginLeft: 6 }}>Pending approval</span>}</td>
                    <td onClick={e => e.stopPropagation()}>{cur && <button className="sm" onClick={() => openFile(cur, true)}>Download</button>}</td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>

      {open && <DocumentModal doc={open} canManage={canApprove} onClose={() => setOpen(null)} onChanged={() => { load() }} />}
      {creating && <CreateModal onClose={() => setCreating(false)} onCreated={(d) => { setCreating(false); load(); setOpen(d) }} />}
    </div>
  )
}

function CreateModal({ onClose, onCreated }) {
  const { profile, role, logAction } = useAuth()
  const [f, setF] = useState({ document_type: 'M&P Manual', status: 'Active' })
  const [busy, setBusy] = useState(false)
  const set = (k, v) => setF(p => ({ ...p, [k]: v }))
  async function save() {
    if (!(f.title || '').trim()) { toast('Enter a title', 'warning'); return }
    setBusy(true)
    const { data, error } = await supabase.from('controlled_documents').insert({
      document_code: f.document_code || null, title: f.title, document_type: f.document_type || null, category: f.category || null,
      status: f.status || 'Active', owner_role: f.owner_role || role || null, owner: f.owner || profile?.name || null,
      created_by: profile?.name || 'Staff',
    }).select().single()
    setBusy(false)
    if (error) { failed("Couldn't create the document", error.message); return }
    if (logAction) logAction('Controlled document created', 'controlled_documents', data.id, `${data.document_code || ''} ${data.title}`.trim(), {})
    onCreated(data)
  }
  return (
    <Modal title="New controlled document" onClose={busy ? null : onClose}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12 }}>
        <div className="field"><label>Document code</label><input value={f.document_code || ''} onChange={e => set('document_code', e.target.value)} placeholder="e.g. MP-01" /></div>
        <div className="field"><label>Type</label><select value={f.document_type} onChange={e => set('document_type', e.target.value)}>{DOC_TYPES.map(x => <option key={x}>{x}</option>)}</select></div>
        <div className="field" style={{ gridColumn: '1 / -1' }}><label>Title *</label><input value={f.title || ''} onChange={e => set('title', e.target.value)} /></div>
        <div className="field"><label>Category</label><input value={f.category || ''} onChange={e => set('category', e.target.value)} /></div>
        <div className="field"><label>Owner</label><input value={f.owner || ''} onChange={e => set('owner', e.target.value)} placeholder="role / person" /></div>
      </div>
      <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
        <button className="primary" onClick={save} disabled={busy}>{busy ? 'Saving…' : 'Create'}</button>
        <button onClick={onClose} disabled={busy}>Cancel</button>
      </div>
      <div style={{ fontSize: 13, color: 'var(--text3)', marginTop: 8 }}>After creating, open the document to upload its first revision.</div>
    </Modal>
  )
}

function DocumentModal({ doc, canManage, onClose, onChanged }) {
  const { profile, role, user, logAction } = useAuth()
  const [revs, setRevs] = useState([])
  const [loading, setLoading] = useState(true)
  const [upForm, setUpForm] = useState(null)
  const [busy, setBusy] = useState(false)

  useEffect(() => { loadRevs() }, [doc.id])
  async function loadRevs() {
    setLoading(true)
    const { data } = await supabase.from('controlled_document_revisions').select('*').eq('document_id', doc.id).order('uploaded_at', { ascending: false })
    setRevs(data || []); setLoading(false)
  }
  const current = revs.find(r => r.status === 'Current')
  const nextRevNo = () => { let m = 0; revs.forEach(r => { const n = parseInt(r.revision_no, 10); if (!isNaN(n)) m = Math.max(m, n) }); return String(m + 1) }

  async function uploadRevision() {
    const f = upForm
    if (!f.file) { toast('Choose a file', 'warning'); return }
    setBusy(true)
    const path = `${doc.id}/${Date.now()}_${f.file.name.replace(/[^\w.\-]/g, '_')}`
    const up = await supabase.storage.from('controlled-documents').upload(path, f.file)
    if (up.error) { setBusy(false); failed('Upload failed', up.error.message); return }
    // Supersede the previous Current revision, then insert the new Current. (One Current per document.)
    await supabase.from('controlled_document_revisions').update({ status: 'Superseded' }).eq('document_id', doc.id).eq('status', 'Current')
    const { data: rev, error } = await supabase.from('controlled_document_revisions').insert({
      document_id: doc.id, revision_no: f.revision_no || nextRevNo(), revision_date: f.revision_date || null, effective_date: f.effective_date || null,
      revision_summary: f.revision_summary || null, prepared_by: f.prepared_by || null, reviewed_by: f.reviewed_by || null,
      file_path: path, file_name: f.file.name, file_mime_type: f.file.type || null, file_size: f.file.size,
      status: 'Current', uploaded_by: profile?.name || 'Staff', uploaded_by_user_id: user?.id || null,
    }).select().single()
    if (error) { setBusy(false); failed("Couldn't save", error.message); return }
    await supabase.from('controlled_documents').update({ current_revision_id: rev.id, status: 'Active', updated_at: new Date().toISOString() }).eq('id', doc.id)
    setBusy(false)
    if (logAction) {
      logAction('Controlled document revision uploaded', 'controlled_documents', doc.id, `${doc.document_code || ''} Rev ${rev.revision_no}`.trim(), {})
      logAction('Controlled document revision made current', 'controlled_documents', doc.id, `${doc.document_code || ''} Rev ${rev.revision_no}`.trim(), {})
      if (current) logAction('Controlled document revision superseded', 'controlled_documents', doc.id, `${doc.document_code || ''} Rev ${current.revision_no}`.trim(), {})
    }
    setUpForm(null); loadRevs(); onChanged()
  }

  // Approval is never typed. The RPC stamps the caller's own identity and today's date, refuses if the
  // caller uploaded this revision, and the DB trigger blocks any attempt to write those fields directly.
  const canApproveNow = (rev) => !!rev && APPROVER_ROLES.includes(role || '')
    && rev.approval_status !== 'Approved' && rev.approval_status !== 'Approved (historical record)'
    && rev.uploaded_by_user_id !== user?.id

  async function approveRevision(rev) {
    if (!window.confirm(`Approve ${doc.document_code || ''} Rev ${rev.revision_no || ''} as the current controlled copy?\n\nAuditPM will record your name and today's date. This cannot be edited afterwards.`)) return
    setBusy(true)
    const { error } = await supabase.rpc('approve_controlled_revision', { p_rev: rev.id })
    setBusy(false)
    if (error) { failed("Couldn't record the approval", error.message); return }
    if (logAction) logAction('Controlled document revision approved', 'controlled_documents', doc.id, `${doc.document_code || ''} Rev ${rev.revision_no || ''}`.trim(), {})
    loadRevs(); onChanged()
  }

  async function withdraw() {
    if (!window.confirm('Withdraw this document? It will be marked Withdrawn and stay in the register with its files still viewable, as history. To remove it and its files entirely instead, use Delete document.')) return
    await supabase.from('controlled_documents').update({ status: 'Withdrawn', updated_at: new Date().toISOString() }).eq('id', doc.id)
    await supabase.from('controlled_document_revisions').update({ status: 'Withdrawn' }).eq('document_id', doc.id).eq('status', 'Current')
    if (logAction) logAction('Controlled document withdrawn', 'controlled_documents', doc.id, `${doc.document_code || ''} ${doc.title}`.trim(), {})
    loadRevs(); onChanged()
  }

  // Permanent delete — removes the file(s) from storage and the DB row(s). Unlike Withdraw, nothing is left
  // viewable afterward. Intended for sensitive one-off uploads (e.g. ACRA, PI insurance shared for an SAC
  // assessment) that should not remain in the register once no longer needed, and for clearing storage.
  async function deleteRevision(rev) {
    if (!window.confirm(`Permanently delete revision ${rev.revision_no || ''}? This removes its file from storage and cannot be undone — it will not remain viewable, unlike Withdraw.`)) return
    setBusy(true)
    if (rev.file_path) {
      const rm = await supabase.storage.from('controlled-documents').remove([rev.file_path])
      if (rm.error) { setBusy(false); failed("Couldn't delete the file", rm.error.message); return }
    }
    const { error } = await supabase.from('controlled_document_revisions').delete().eq('id', rev.id)
    if (error) { setBusy(false); failed("Couldn't delete", error.message); return }
    if (rev.status === 'Current') {
      await supabase.from('controlled_documents').update({ current_revision_id: null, updated_at: new Date().toISOString() }).eq('id', doc.id)
    }
    if (logAction) logAction('Controlled document revision deleted', 'controlled_documents', doc.id, `${doc.document_code || ''} Rev ${rev.revision_no || ''}`.trim(), {})
    setBusy(false)
    loadRevs(); onChanged()
  }

  async function deleteDocument() {
    if (!window.confirm(`Permanently delete "${doc.title}" and all ${revs.length} revision(s)? This removes every file from storage and cannot be undone. For a document you simply no longer use, Withdraw it instead so its history stays on record — use Delete only for something that should not remain in the register at all.`)) return
    setBusy(true)
    const paths = revs.map(r => r.file_path).filter(Boolean)
    if (paths.length) {
      const rm = await supabase.storage.from('controlled-documents').remove(paths)
      if (rm.error) { setBusy(false); failed("Couldn't delete the files", rm.error.message); return }
    }
    const { error } = await supabase.from('controlled_documents').delete().eq('id', doc.id)
    if (error) { setBusy(false); failed("Couldn't delete", error.message); return }
    if (logAction) logAction('Controlled document deleted', 'controlled_documents', doc.id, `${doc.document_code || ''} ${doc.title}`.trim(), {})
    setBusy(false)
    onChanged(); onClose()
  }

  const setU = (k, v) => setUpForm(p => ({ ...p, [k]: v }))
  return (
    <Modal title={`${doc.document_code || ''} ${doc.title || 'Document'}`.trim()} wide onClose={busy ? null : onClose}>
      <div style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 12, display: 'flex', flexWrap: 'wrap', gap: '2px 18px' }}>
        <span><b>Type</b> {doc.document_type || '—'}</span><span><b>Category</b> {doc.category || '—'}</span>
        <span><b>Owner</b> {doc.owner || '—'}</span><span><b>Status</b> {doc.status}</span>
        {current && <><span><b>Current</b> Rev {current.revision_no || '—'}</span><span><b>Effective</b> {fmt(current.effective_date)}</span></>}
      </div>
      {current && <ApprovalPanel rev={current} canApproveNow={canApproveNow(current)} onApprove={approveRevision} busy={busy} />}
      {current && <div style={{ marginBottom: 12 }}>
        <button className="sm primary" onClick={() => openFile(current, false)}>View current</button>{' '}
        <button className="sm" onClick={() => openFile(current, true)}>Download current</button>
      </div>}

      {canManage && (upForm
        ? <div className="card" style={{ background: 'var(--bg2)', padding: 12, marginBottom: 12 }}>
            <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 8 }}>Upload new revision</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10 }}>
              <div className="field"><label>Revision no.</label><input value={upForm.revision_no || ''} onChange={e => setU('revision_no', e.target.value)} placeholder={`default ${nextRevNo()}`} /></div>
              <div className="field"><label>Revision date</label><input type="date" value={upForm.revision_date || ''} onChange={e => setU('revision_date', e.target.value)} /></div>
              <div className="field"><label>Effective date</label><input type="date" value={upForm.effective_date || ''} onChange={e => setU('effective_date', e.target.value)} /></div>
              <div className="field"><label>Prepared by</label><input value={upForm.prepared_by || ''} onChange={e => setU('prepared_by', e.target.value)} /></div>
              <div className="field"><label>Reviewed by</label><input value={upForm.reviewed_by || ''} onChange={e => setU('reviewed_by', e.target.value)} /></div>
              <div className="field" style={{ gridColumn: '1 / -1' }}>
                <div style={{ fontSize: 13, color: 'var(--text2)', background: 'var(--bg3)', borderRadius: 6, padding: '8px 10px' }}>
                  Approval is not entered here. Once uploaded, the revision stays <b>Pending approval</b> until an authorised approver signs in and approves it - AuditPM then records their name and the date itself.
                </div>
              </div>
              <div className="field" style={{ gridColumn: '1 / -1' }}><label>Summary of changes</label><textarea rows={2} value={upForm.revision_summary || ''} onChange={e => setU('revision_summary', e.target.value)} /></div>
              <div className="field" style={{ gridColumn: '1 / -1' }}><label>File</label><input type="file" onChange={e => setU('file', e.target.files[0] || null)} /></div>
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
              <button className="primary" onClick={uploadRevision} disabled={busy}>{busy ? 'Uploading…' : 'Upload as Current'}</button>
              <button onClick={() => setUpForm(null)} disabled={busy}>Cancel</button>
            </div>
          </div>
        : <div style={{ marginBottom: 12, display: 'flex', gap: 8 }}>
            <button className="sm primary" onClick={() => setUpForm({ revision_no: nextRevNo(), revision_date: new Date().toISOString().split('T')[0] })}>+ Upload new revision</button>
            {doc.status !== 'Withdrawn' && <button className="sm danger" onClick={withdraw} disabled={busy}>Withdraw document</button>}
            <button className="sm danger" onClick={deleteDocument} disabled={busy} title="Permanently delete this document and all its files">Delete document</button>
          </div>)}

      <div style={{ fontWeight: 600, fontSize: 13, margin: '4px 0 6px' }}>Revision history</div>
      <div className="table-wrap">
        <table>
          <thead><tr><th>Rev</th><th>Rev date</th><th>Effective</th><th>Summary</th><th>Prepared / Reviewed</th><th>Approval</th><th>Status</th><th></th></tr></thead>
          <tbody>
            {loading && <tr><td colSpan={8} style={{ textAlign: 'center', padding: 16, color: 'var(--text3)' }}>Loading…</td></tr>}
            {!loading && revs.length === 0 && <tr><td colSpan={8} style={{ textAlign: 'center', padding: 16, color: 'var(--text3)' }}>No revisions uploaded yet.</td></tr>}
            {revs.map(r => (
              <tr key={r.id}>
                <td style={{ fontWeight: 500 }}>{r.revision_no || '—'}</td>
                <td style={{ fontSize: 13, color: 'var(--text2)' }}>{fmt(r.revision_date)}</td>
                <td style={{ fontSize: 13, color: 'var(--text2)' }}>{fmt(r.effective_date)}</td>
                <td style={{ fontSize: 13, maxWidth: 220 }}>{r.revision_summary || '—'}</td>
                <td style={{ fontSize: 13, color: 'var(--text2)' }}>{[r.prepared_by, r.reviewed_by].map(x => x || '—').join(' / ')}</td>
                <td style={{ fontSize: 13 }}>{r.approval_status === 'Approved'
                  ? <span style={{ color: '#27500a' }}>Approved by {r.approved_by}<br />{fmt(r.approved_at)}</span>
                  : r.approval_status === 'Approved (historical record)'
                    ? <span style={{ color: 'var(--text2)' }}>{r.approved_by}<br />{fmt(r.approved_at)} (historical)</span>
                    : <span style={{ color: '#8a5a00' }}>Pending approval</span>}</td>
                <td><span className={`badge ${r.status === 'Current' ? 'badge-ok' : 'badge'}`}>{r.status}</span></td>
                <td style={{ whiteSpace: 'nowrap' }}>
                  <button className="sm" onClick={() => openFile(r, false)}>View</button>{' '}
                  <button className="sm" onClick={() => openFile(r, true)}>Download</button>
                  {canManage && <>{' '}<button className="sm danger" onClick={() => deleteRevision(r)} disabled={busy} title="Permanently delete this revision's file">Delete</button></>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Modal>
  )
}

// Shows the approval state of the current revision, and the Approve button to the one person entitled
// to press it. Nothing here is editable - every value comes from approve_controlled_revision().
function ApprovalPanel({ rev, canApproveNow, onApprove, busy }) {
  const approved = rev.approval_status === 'Approved'
  const historical = rev.approval_status === 'Approved (historical record)'
  const bg = approved ? '#EAF3DE' : historical ? 'var(--bg2)' : '#FFF6E5'
  const col = approved ? '#27500a' : historical ? 'var(--text2)' : '#8a5a00'
  return (
    <div style={{ background: bg, borderRadius: 8, padding: '10px 14px', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
      <div style={{ fontSize: 13, color: col, flex: 1, minWidth: 240 }}>
        {approved && <>
          <b>Approved by {rev.approved_by}</b> on {fmt(rev.approved_at)}{rev.approved_at_ts ? ` at ${format(parseISO(rev.approved_at_ts), 'h:mm a')}` : ''}
          <div style={{ fontSize: 13, marginTop: 2 }}>Recorded by AuditPM from the approver's own login. It cannot be edited or entered by hand.</div>
        </>}
        {historical && <>
          <b>Approved by {rev.approved_by}</b> on {fmt(rev.approved_at)}
          <div style={{ fontSize: 13, marginTop: 2 }}>Historical record - this approval predates AuditPM and was entered from the signed copy.</div>
        </>}
        {!approved && !historical && <>
          <b>Pending approval</b>
          <div style={{ fontSize: 13, marginTop: 2 }}>Uploaded but not yet approved. Approval must be recorded by an authorised approver, who cannot be the person who uploaded it.</div>
        </>}
      </div>
      {canApproveNow && <button className="sm primary" disabled={busy} onClick={() => onApprove(rev)}>Approve this revision</button>}
    </div>
  )
}

function Modal({ title, children, onClose, wide }) {
  return (
    <div className="modal-overlay" onClick={() => onClose && onClose()} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '40px 16px', overflowY: 'auto', zIndex: 60 }}>
      <div className="card" onClick={e => e.stopPropagation()} style={{ maxWidth: wide ? 820 : 520, width: '100%' }}>
        <h2 style={{ marginBottom: 14 }}>{title}</h2>
        {children}
      </div>
    </div>
  )
}
